/**
 * The application-wide search panel — the ONE search surface, shared by the
 * platform's Run view and the deployed/downloaded runtime.
 *
 * It renders results and nothing else: the transport is injected (`search`)
 * and so is what "open this" means (`onOpenHit`), because the two hosts route
 * differently and neither routing belongs in this package.
 *
 * Two things here are behaviour, not decoration:
 *
 * - **The view choice is remembered.** List and card answer different
 *   questions — "which of these is it" vs "read the passage" — and a reader
 *   who picked one is telling you how they search, not how they wanted this
 *   one query displayed. It is stored in `localStorage`, so the NEXT search,
 *   in the next session, opens the way they left it.
 * - **An empty result explains itself.** A corpus that could not run reports
 *   why (`sources[].reason`), and that is shown instead of an unqualified
 *   "no results" — "records aren't indexed in this application yet" is
 *   somebody's to fix, and "nothing matched" is not.
 *
 * Results are permission-trimmed by the SERVER (object grants for records, the
 * repository ACL for documents). Nothing is filtered here; a hit that arrives
 * is a hit the caller is entitled to.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import {
  AlertTriangle, FileText, FolderArchive, Info, LayoutGrid, List, Loader2,
  Search, X,
} from 'lucide-react'
import type {
  GlobalSearchClient, GlobalSearchHit, GlobalSearchRequest,
  GlobalSearchResponse, GlobalSearchSource, SearchKind, SearchResultView,
} from './contracts'
import { HighlightedText } from './HighlightedText'
import { UI_ATTR } from './highlight'
import { setActiveHighlight } from './highlightStore'

/** Keystrokes settle before a query goes out. */
const DEBOUNCE_MS = 350
/**
 * Two characters matched most of a corpus and produced noise; three is where a
 * query starts to mean something, and it also removes the burst of near-useless
 * searches a reader fires on their way to a real one.
 */
const MIN_QUERY_LEN = 3
const VIEW_KEY = 'twasta.search.view'

/** Screens in the sidebar list answer the moment a key is pressed. */
const MAX_SCREEN_ROWS = 8
/** With nothing typed yet, the first few menu entries are offered to jump to. */
const SUGGESTED_SCREENS = 6

type KindFilter = 'all' | 'screen' | SearchKind

/**
 * One entry of the application's own menu, searched LOCALLY (no request) so a
 * reader looking for a screen is never made to wait for the record / document
 * search that runs beside it.
 */
export interface SearchScreen {
  id: string
  obj_name: string
  title: string
  /** Where it sits in the menu, e.g. "Appointments › Reports". */
  group: string
}

interface ScreenRowData { screen: SearchScreen; at: number; len: number }

export interface GlobalSearchPanelProps {
  open: boolean
  onClose: () => void
  /** Host transport — `/api/appsearch/search` or `/app/{p}/api/search`. */
  search: GlobalSearchClient
  /**
   * Open one result. The host turns the hit into navigation: a record into the
   * transaction editor (`record_key` is the txn_id), a document into its
   * repository (`repo_obj` + `document_id`).
   */
  onOpenHit: (hit: GlobalSearchHit) => void
  /** Shown above the input. */
  title?: string
  placeholder?: string
  /** Restrict the whole panel to one document repository. */
  repo?: string
  /** Suffix so two apps in one browser can keep different view preferences. */
  viewStorageKey?: string
  limit?: number
  /**
   * The application's menu. When given, the panel finds screens first and
   * instantly, and the server search for records and documents fills in below.
   */
  screens?: SearchScreen[]
  /** Open a screen found in `screens`. */
  onOpenScreen?: (screen: SearchScreen) => void
}

function readView(key: string): SearchResultView {
  if (typeof localStorage === 'undefined') return 'list'
  try {
    return localStorage.getItem(key) === 'card' ? 'card' : 'list'
  } catch {
    return 'list'
  }
}

function writeView(key: string, view: SearchResultView): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(key, view)
  } catch {
    // Blocked site data costs the preference and nothing else.
  }
}

export function GlobalSearchPanel({
  open, onClose, search, onOpenHit, title = 'Search',
  placeholder,
  repo = '', viewStorageKey = '', limit = 25, screens, onOpenScreen,
}: GlobalSearchPanelProps) {
  const hasScreens = !!(screens && screens.length && onOpenScreen)
  const inputPlaceholder = placeholder
    ?? (hasScreens ? 'Search screens, records and documents…' : 'Search records and documents…')
  const storageKey = viewStorageKey ? `${VIEW_KEY}.${viewStorageKey}` : VIEW_KEY
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<KindFilter>('all')
  const [view, setView] = useState<SearchResultView>(() => readView(storageKey))
  const [result, setResult] = useState<GlobalSearchResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const aliveRef = useRef(true)
  const runningRef = useRef(false)
  const queuedRef = useRef<GlobalSearchRequest | null>(null)
  /** The query the panel currently WANTS shown; '' while below the minimum. */
  const latestRef = useRef('')
  const abortRef = useRef<AbortController | null>(null)
  const screenCountRef = useRef(0)

  const hits = result?.hits || []
  const terms = result?.terms || []

  // Menu matches are computed in the render itself — synchronous, so they are
  // on screen with the very keystroke, while the debounced server search below
  // is still on its way.
  const screenRows = useMemo<ScreenRowData[]>(() => {
    if (!hasScreens || (kind !== 'all' && kind !== 'screen')) return []
    const q = query.trim().toLowerCase()
    if (!q) {
      return (screens as SearchScreen[]).slice(0, SUGGESTED_SCREENS)
        .map((screen) => ({ screen, at: -1, len: 0 }))
    }
    const words = q.split(/\s+/).filter(Boolean)
    const ranked: Array<ScreenRowData & { rank: number }> = []
    for (const screen of screens as SearchScreen[]) {
      const title = screen.title.toLowerCase()
      const at = title.indexOf(q)
      if (at >= 0) {
        ranked.push({ screen, at, len: q.length, rank: at === 0 ? 0 : 1 })
        continue
      }
      const hay = `${title} ${screen.group} ${screen.obj_name}`.toLowerCase()
      if (words.every((w) => hay.includes(w))) ranked.push({ screen, at: -1, len: 0, rank: 2 })
    }
    return ranked.sort((a, b) => a.rank - b.rank).slice(0, MAX_SCREEN_ROWS)
  }, [hasScreens, screens, query, kind])
  screenCountRef.current = screenRows.length
  const total = screenRows.length + hits.length

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 0)
  }, [open])

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      // Closing the panel DOES cancel: the server checks the connection
      // between repositories and stops when the caller has gone.
      abortRef.current?.abort()
    }
  }, [])

  /**
   * Run searches ONE AT A TIME, latest query wins.
   *
   * Aborting a superseded request is not enough on its own: an abort closes
   * the browser's end, but the server has already started, and every abandoned
   * keystroke still ran a full search. A reader typing "Shift Handover" put
   * eight concurrent searches on a connection pool sized `5 + 10`, and the
   * ninth spent up to 30 seconds queueing for a connection — which on screen
   * is a search that appears to hang.
   *
   * So a newer query does not race the one in flight; it QUEUES, and is issued
   * when that one settles. At most one search per panel is ever outstanding,
   * the last query typed is always the one displayed, and the intermediate
   * ones are never sent at all.
   */
  const run = useCallback(async (request: GlobalSearchRequest) => {
    if (runningRef.current) {
      queuedRef.current = request
      return
    }
    runningRef.current = true
    setLoading(true)
    try {
      let current: GlobalSearchRequest | null = request
      while (current) {
        const controller = new AbortController()
        abortRef.current = controller
        try {
          // Promise.resolve so a transport that throws SYNCHRONOUSLY becomes a
          // rejection this chain reports, rather than escaping and leaving the
          // panel spinning on a request that was never made.
          const res = await Promise.resolve().then(
            () => search(current as GlobalSearchRequest, controller.signal),
          )
          // Only paint if nothing newer is already waiting — otherwise the
          // reader sees results for a query they have moved past.
          // Paint only the answer to the query the reader is still on. The
          // server echoes the query it ran, so this compares like with like
          // rather than trusting arrival order — a reader who typed past the
          // minimum and back below it must not be shown the older answer.
          if (aliveRef.current && !queuedRef.current
              && res.query === latestRef.current) {
            setResult(res)
            setError('')
            // A reader already walking the screen rows keeps their place; the
            // late arrival of record hits must not yank the cursor.
            setCursor((c) => (c < screenCountRef.current ? c : 0))
          }
        } catch (err: unknown) {
          if (aliveRef.current && !queuedRef.current && !controller.signal.aborted
              && (current as GlobalSearchRequest).query === latestRef.current) {
            setResult(null)
            setError(err instanceof Error && err.message
              ? err.message
              : 'The search could not be completed.')
          }
        }
        current = queuedRef.current
        queuedRef.current = null
      }
    } finally {
      runningRef.current = false
      if (aliveRef.current) setLoading(false)
    }
  }, [search])

  // Debounce, then hand the query to the single-flight runner.
  useEffect(() => {
    if (!open) return undefined
    const trimmed = query.trim()
    latestRef.current = trimmed.length < MIN_QUERY_LEN || kind === 'screen' ? '' : trimmed
    if (trimmed.length < MIN_QUERY_LEN || kind === 'screen') {
      queuedRef.current = null
      setResult(null)
      setError('')
      if (!runningRef.current) setLoading(false)
      return undefined
    }
    const timer = window.setTimeout(() => {
      void run({
        query: trimmed,
        limit,
        kinds: kind === 'all' ? undefined : [kind],
        repo: repo || undefined,
      })
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [open, query, kind, repo, limit, run])

  // New text, new first choice: the best screen match is always the default.
  useEffect(() => { setCursor(0) }, [query, kind])

  const chooseScreen = useCallback((screen: SearchScreen) => {
    onOpenScreen?.(screen)
    onClose()
  }, [onOpenScreen, onClose])

  const choose = useCallback((hit: GlobalSearchHit) => {
    // A hit with nowhere to go stays put rather than closing the panel on a
    // navigation that will not happen.
    if (hit.openable === false) return
    // The terms travel with the navigation: the page that opens highlights
    // what the RESULT LIST highlighted, rather than re-deriving the match.
    setActiveHighlight({
      query: result?.query || query.trim(),
      terms: terms.length ? terms : [query.trim()],
      label: hit.title,
    })
    onOpenHit(hit)
    onClose()
  }, [onOpenHit, onClose, result, query, terms])

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (!total) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((c) => (c + 1) % total)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((c) => (c - 1 + total) % total)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (cursor < screenRows.length) {
        chooseScreen(screenRows[cursor].screen)
      } else {
        const hit = hits[cursor - screenRows.length]
        if (hit) choose(hit)
      }
    }
  }, [total, hits, screenRows, cursor, choose, chooseScreen, onClose])

  // Keep the keyboard cursor visible when it walks past the fold.
  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-hit-index="${cursor}"]`)
    node?.scrollIntoView({ block: 'nearest' })
  }, [cursor, view])

  const pickView = useCallback((next: SearchResultView) => {
    setView(next)
    writeView(storageKey, next)
  }, [storageKey])

  /**
   * What was actually searched, and what was not.
   *
   * Shown as INFORMATION, not as a warning. "Record search is switched off for
   * this application" is a true and useful statement about scope — it is not
   * an error, and rendering it as one on every keystroke made a working search
   * look broken. The reader needs it most when the result list is empty, which
   * is exactly when an unqualified "nothing matched" is misleading.
   */
  const scope = useMemo(() => {
    const sources = result?.sources || []
    const searched = sources.filter((s) => s.available)
    const skipped = sources.filter((s) => !s.available && s.reason)
    return { searched, skipped, any: sources.length > 0 }
  }, [result])

  if (!open) return null

  return (
    <div {...{ [UI_ATTR]: '' }} style={overlay} role="dialog" aria-modal="true"
      aria-label={title} onMouseDown={onClose}>
      <div style={panel} onMouseDown={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
        <div style={header}>
          <Search size={16} style={{ opacity: 0.6, flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={inputPlaceholder}
            style={input}
            aria-label={title}
            autoComplete="off"
            spellCheck={false}
          />
          {loading ? (
            <Loader2 size={15} style={{ opacity: 0.6, animation: 'spin 1s linear infinite' }} />
          ) : null}
          <button type="button" onClick={onClose} style={iconBtn}
            title="Close (Esc)" aria-label="Close search">
            <X size={16} />
          </button>
        </div>

        <div style={toolbar}>
          <div style={{ display: 'flex', gap: 4 }}>
            <Chip active={kind === 'all'} onClick={() => setKind('all')}>Everything</Chip>
            {hasScreens ? (
              <Chip active={kind === 'screen'} onClick={() => setKind('screen')}>Screens</Chip>
            ) : null}
            <Chip active={kind === 'record'} onClick={() => setKind('record')}>Records</Chip>
            <Chip active={kind === 'document'} onClick={() => setKind('document')}>Documents</Chip>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {result || screenRows.length ? (
              <span style={{ fontSize: 13, opacity: 0.6 }}>
                {(result?.count ?? 0) + (query.trim() ? screenRows.length : 0)}
                {' '}{(result?.count ?? 0) + (query.trim() ? screenRows.length : 0) === 1
                  ? 'result' : 'results'}
              </span>
            ) : null}
            <div style={viewGroup} role="group" aria-label="Result layout">
              <button type="button" onClick={() => pickView('list')}
                title="List view" aria-pressed={view === 'list'}
                style={{ ...viewBtn, ...(view === 'list' ? viewBtnOn : null) }}>
                <List size={14} />
              </button>
              <button type="button" onClick={() => pickView('card')}
                title="Preview cards" aria-pressed={view === 'card'}
                style={{ ...viewBtn, ...(view === 'card' ? viewBtnOn : null) }}>
                <LayoutGrid size={14} />
              </button>
            </div>
          </div>
        </div>

        <div ref={listRef} style={body}>
          {/* A failed REQUEST is the only thing that warrants a warning. */}
          {error ? <Notice tone="error">{error}</Notice> : null}

          {screenRows.length ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={sectionTitle}>{query.trim() ? 'Screens' : 'Jump to'}</div>
              {screenRows.map((r, i) => (
                <ScreenRow key={r.screen.id} data={r} index={i}
                  active={i === cursor}
                  onHover={() => setCursor(i)}
                  onPick={() => chooseScreen(r.screen)} />
              ))}
            </div>
          ) : null}

          {kind === 'screen' && query.trim() && !screenRows.length ? (
            <Empty>
              <div style={{ fontWeight: 600, color: 'var(--color-text, #0f172a)' }}>
                No screen matches “{query.trim()}”.
              </div>
            </Empty>
          ) : kind === 'screen' ? null
          : query.trim().length < MIN_QUERY_LEN ? (
            screenRows.length ? (
              query.trim() ? (
                <div style={moreHint}>
                  Keep typing to also search records and documents ({MIN_QUERY_LEN}+ characters).
                </div>
              ) : null
            ) : (
              <Empty>
                Type at least {MIN_QUERY_LEN} characters. Records are matched by
                meaning, documents by their content and title — you only see what
                your profile allows.
              </Empty>
            )
          ) : loading && !hits.length && screenRows.length ? (
            <div style={moreHint}>
              <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', marginRight: 6, verticalAlign: -2 }} />
              Searching records and documents…
            </div>
          ) : !loading && !hits.length && !error && !screenRows.length ? (
            <Empty>
              <div style={{ fontWeight: 600, color: 'var(--color-text, #0f172a)' }}>
                Nothing matched “{query.trim()}”.
              </div>
              {scope.searched.length ? (
                <div style={{ marginTop: 6 }}>
                  Searched {scope.searched.map(sourceLabel).join(', ')}.
                </div>
              ) : null}
            </Empty>
          ) : null}

          {/* Anything that could NOT be searched, stated plainly and once —
              under the results, where it explains a short list rather than
              interrupting a good one. */}
          {scope.skipped.length ? (
            <div style={skippedBox}>
              <Info size={13} style={{ flexShrink: 0, marginTop: 1, opacity: 0.7 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {scope.skipped.map((source) => (
                  <div key={source.name}>
                    <strong style={{ fontWeight: 600 }}>{sourceLabel(source)}</strong>
                    {' — '}{source.reason}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {screenRows.length && hits.length ? (
            <div style={sectionTitle}>Records and documents</div>
          ) : null}
          {view === 'list' ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {hits.map((hit, i) => (
                <ListRow key={rowKey(hit, i)} hit={hit} index={screenRows.length + i}
                  active={screenRows.length + i === cursor}
                  onHover={() => setCursor(screenRows.length + i)}
                  onPick={() => choose(hit)} />
              ))}
            </div>
          ) : (
            <div style={cardGrid}>
              {hits.map((hit, i) => (
                <PreviewCard key={rowKey(hit, i)} hit={hit} index={screenRows.length + i}
                  active={screenRows.length + i === cursor}
                  onHover={() => setCursor(screenRows.length + i)}
                  onPick={() => choose(hit)} />
              ))}
            </div>
          )}
        </div>

        <div style={footer}>
          <span>↑ ↓ to move · Enter to open · Esc to close</span>
          <span style={{ opacity: 0.7, textAlign: 'right' }}>
            {scope.any && hits.length
              ? `Searched ${scope.searched.map(sourceLabel).join(', ') || 'nothing'}`
              : 'Matching text is highlighted on the screen that opens.'}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * A source's human name. The wire name is `record` or `document:SOP_MANUALS`;
 * a reader has never heard of either spelling.
 */
function sourceLabel(source: GlobalSearchSource): string {
  const name = source.name || ''
  if (name === 'record') return 'Records'
  const repo = name.startsWith('document:') ? name.slice('document:'.length) : name
  return repo.replace(/_/g, ' ')
}

const skippedBox: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  margin: '4px 14px 8px',
  padding: '7px 10px',
  borderRadius: 8,
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--color-text-secondary, #64748b)',
  background: 'var(--color-bg-secondary, #f8fafc)',
  border: '1px solid var(--color-border, #e2e8f0)',
}

/** Said once, in the tooltip, rather than as a badge on every such row. */
const NOT_OPENABLE =
  'This record is indexed from a table that has no screen in this '
  + 'application, so there is nothing to open.'

const sectionTitle: CSSProperties = {
  padding: '8px 14px 4px',
  fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--color-text-secondary, #64748b)',
}

const moreHint: CSSProperties = {
  margin: '6px 14px 2px', padding: '8px 10px', borderRadius: 8, fontSize: 13,
  color: 'var(--color-text-secondary, #64748b)',
  background: 'var(--color-bg-secondary, #f8fafc)',
}

const notOpenable: CSSProperties = { cursor: 'default', opacity: 0.75 }

function rowKey(hit: GlobalSearchHit, index: number): string {
  return `${hit.kind}:${hit.obj_name}:${hit.record_key || hit.document_id}:${index}`
}

function KindIcon({ hit }: { hit: GlobalSearchHit }) {
  return hit.kind === 'document'
    ? <FolderArchive size={15} style={{ opacity: 0.75 }} />
    : <FileText size={15} style={{ opacity: 0.75 }} />
}

function ListRow({ hit, index, active, onHover, onPick }: {
  hit: GlobalSearchHit
  index: number
  active: boolean
  onHover: () => void
  onPick: () => void
}) {
  return (
    <button
      type="button"
      data-hit-index={index}
      onMouseEnter={onHover}
      onClick={onPick}
      disabled={hit.openable === false}
      title={hit.openable === false ? NOT_OPENABLE : undefined}
      style={{
        ...row,
        ...(active ? rowActive : null),
        ...(hit.openable === false ? notOpenable : null),
      }}
    >
      <span style={{ marginTop: 2, flexShrink: 0 }}><KindIcon hit={hit} /></span>
      <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={rowTitle}>{hit.title}</span>
        {hit.subtitle ? <span style={rowSubtitle}>{hit.subtitle}</span> : null}
        {hit.snippet ? (
          <span style={rowSnippet}>
            <HighlightedText text={hit.snippet} matches={hit.matches} />
          </span>
        ) : null}
      </span>
      <MatchCount hit={hit} />
    </button>
  )
}

function ScreenRow({ data, index, active, onHover, onPick }: {
  data: ScreenRowData
  index: number
  active: boolean
  onHover: () => void
  onPick: () => void
}) {
  const { screen, at, len } = data
  return (
    <button
      type="button"
      data-hit-index={index}
      onMouseEnter={onHover}
      onClick={onPick}
      style={{ ...row, alignItems: 'center', ...(active ? rowActive : null) }}
    >
      <span style={{ flexShrink: 0 }}><FileText size={15} style={{ opacity: 0.75 }} /></span>
      <span style={{ ...rowTitle, flex: 1, minWidth: 0, fontWeight: 500 }}>
        <HighlightedText text={screen.title} matches={at >= 0 ? [[at, at + len]] : undefined} />
      </span>
      {screen.group ? <span style={{ ...rowSubtitle, flexShrink: 0, maxWidth: '45%' }}>{screen.group}</span> : null}
    </button>
  )
}

function PreviewCard({ hit, index, active, onHover, onPick }: {
  hit: GlobalSearchHit
  index: number
  active: boolean
  onHover: () => void
  onPick: () => void
}) {
  return (
    <button
      type="button"
      data-hit-index={index}
      onMouseEnter={onHover}
      onClick={onPick}
      disabled={hit.openable === false}
      title={hit.openable === false ? NOT_OPENABLE : undefined}
      style={{
        ...card,
        ...(active ? cardActive : null),
        ...(hit.openable === false ? notOpenable : null),
      }}
    >
      <span style={cardHead}>
        <KindIcon hit={hit} />
        <span style={cardKind}>
          {hit.kind === 'document'
            ? (hit.doctype_label || 'Document')
            : (hit.obj_name || 'Record')}
        </span>
        <MatchCount hit={hit} />
      </span>
      <span style={cardTitle}>{hit.title}</span>
      {hit.subtitle ? <span style={cardSubtitle}>{hit.subtitle}</span> : null}
      <span style={cardBody}>
        <HighlightedText text={hit.snippet} matches={hit.matches} />
      </span>
    </button>
  )
}

/** "4 matches" — the honest count from the full text, not the window. */
function MatchCount({ hit }: { hit: GlobalSearchHit }) {
  if (!hit.total_matches) return null
  return (
    <span style={badge} title={
      `${hit.total_matches} occurrence${hit.total_matches === 1 ? '' : 's'} in this ${
        hit.kind === 'document' ? 'passage' : 'record'}`
    }>
      {hit.total_matches}
    </span>
  )
}

function Chip({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: ReactNode
}) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      style={{ ...chip, ...(active ? chipOn : null) }}>
      {children}
    </button>
  )
}

/** A failed REQUEST. Scope facts are information and render as such above. */
function Notice({ tone, children }: { tone: 'error'; children: ReactNode }) {
  const error = tone === 'error'
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start',
      margin: '8px 12px', padding: '8px 10px', borderRadius: 8, fontSize: 13,
      lineHeight: 1.45,
      background: error ? 'rgba(220, 38, 38, 0.10)' : 'rgba(245, 158, 11, 0.12)',
      border: `1px solid ${error ? 'rgba(220, 38, 38, 0.4)' : 'rgba(245, 158, 11, 0.4)'}`,
      color: 'var(--color-text, #0f172a)',
    }}>
      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div style={{
      padding: '28px 20px', textAlign: 'center', fontSize: 13.5, lineHeight: 1.55,
      color: 'var(--color-text-secondary, #64748b)',
    }}>
      {children}
    </div>
  )
}

// ── styles: platform CSS vars with literal fallbacks, so the panel renders
// correctly both in the platform (vars from globals.css) and in the standalone
// runtime client (no vars defined → light-theme fallbacks).

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 9500,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '8vh 16px 16px',
}

const panel: CSSProperties = {
  width: 'min(820px, 100%)',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 12,
  background: 'var(--color-surface, #ffffff)',
  color: 'var(--color-text, #0f172a)',
  border: '1px solid var(--color-border, #e2e8f0)',
  boxShadow: '0 24px 64px rgba(15, 23, 42, 0.35)',
}

const header: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 12px 10px 14px',
  borderBottom: '1px solid var(--color-border, #e2e8f0)',
}

const input: CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: 'inherit',
  fontSize: 16,
  fontFamily: 'inherit',
}

const toolbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '8px 12px',
  borderBottom: '1px solid var(--color-border, #e2e8f0)',
  background: 'var(--color-bg-secondary, #f8fafc)',
}

const body: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '4px 0 8px',
}

const footer: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  padding: '7px 14px',
  borderTop: '1px solid var(--color-border, #e2e8f0)',
  fontSize: 13,
  color: 'var(--color-text-secondary, #64748b)',
}

const row: CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  width: '100%',
  textAlign: 'left',
  padding: '9px 14px',
  border: 'none',
  borderLeft: '3px solid transparent',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const rowActive: CSSProperties = {
  background: 'var(--color-bg-secondary, #f1f5f9)',
  borderLeftColor: 'var(--color-accent, #2563eb)',
}

const rowTitle: CSSProperties = {
  fontSize: 14.5, fontWeight: 600,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

const rowSubtitle: CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary, #64748b)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

const rowSnippet: CSSProperties = {
  fontSize: 13, lineHeight: 1.45,
  color: 'var(--color-text-secondary, #475569)',
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

const cardGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))',
  gap: 10,
  padding: '10px 14px',
}

const card: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  textAlign: 'left',
  padding: 12,
  borderRadius: 10,
  border: '1px solid var(--color-border, #e2e8f0)',
  background: 'var(--color-bg, #ffffff)',
  color: 'inherit',
  cursor: 'pointer',
  fontFamily: 'inherit',
  minHeight: 128,
}

const cardActive: CSSProperties = {
  borderColor: 'var(--color-accent, #2563eb)',
  boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.16)',
}

const cardHead: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
}

const cardKind: CSSProperties = {
  flex: 1, minWidth: 0,
  fontSize: 13, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--color-text-secondary, #64748b)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

const cardTitle: CSSProperties = {
  fontSize: 14.5, fontWeight: 600, lineHeight: 1.3,
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

const cardSubtitle: CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary, #64748b)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

const cardBody: CSSProperties = {
  fontSize: 13, lineHeight: 1.45,
  color: 'var(--color-text-secondary, #475569)',
  display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

const badge: CSSProperties = {
  flexShrink: 0,
  minWidth: 18, height: 18, padding: '0 5px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 13, fontWeight: 700, lineHeight: 1,
  borderRadius: 9,
  background: 'var(--color-bg-secondary, #e2e8f0)',
  color: 'var(--color-text-secondary, #475569)',
}

const chip: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  border: '1px solid var(--color-border, #e2e8f0)',
  background: 'transparent',
  color: 'var(--color-text-secondary, #64748b)',
}

const chipOn: CSSProperties = {
  background: 'var(--color-accent, #2563eb)',
  borderColor: 'var(--color-accent, #2563eb)',
  color: '#ffffff',
}

const viewGroup: CSSProperties = {
  display: 'inline-flex',
  borderRadius: 8,
  overflow: 'hidden',
  border: '1px solid var(--color-border, #e2e8f0)',
}

const viewBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 24,
  border: 'none',
  background: 'transparent',
  color: 'var(--color-text-secondary, #64748b)',
  cursor: 'pointer',
}

const viewBtnOn: CSSProperties = {
  background: 'var(--color-accent, #2563eb)',
  color: '#ffffff',
}

const iconBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 999,
  border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer',
  flexShrink: 0,
}

export default GlobalSearchPanel
